import React, { useEffect, useRef, useState } from 'react';
import {
  FormInstance, Upload, Button, UploadFile, UploadProps, Alert, App, Modal, Form,
} from 'antd';
import clsx from 'clsx';
import {
  DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined,
} from '@ant-design/icons';
import { RcFile } from 'antd/es/upload';
import ImgCrop, { ImgCropProps } from 'antd-img-crop';
import { NamePath } from 'rc-field-form/lib/interface';
import { getMessageInError } from '../../../../hooks/fetch';
import { getBase64 } from '../../../../utils';
import { useUploadImage } from '../../../../hooks/api/mediaUpload';
import { messages } from '../../../../utils/validations';
import { useMessageError } from '../../../../hooks/common';

import styles from './index.module.scss';

export const getFullFileUrl = (url: string | undefined) => {
  if (isFullUrl(url)) { return url || ''; }

  return `${process.env.REACT_APP_AMAZON_S3_URL}/${url}`;
};

const isFullUrl = (url: string | undefined): boolean => !!(url?.startsWith('http'));

export const generateFileObjFromUrl = (url: string) : UploadFile => ({
  url,
  uid: url,
  name: (url || '').replace('images/', ''),
  status: 'done',
});

interface InputCodeWrapperProps extends React.PropsWithChildren {
  name: NamePath;
  fileList: UploadFile[];
  setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  required?: boolean;
  form?: FormInstance;
  accept?: string;
  uploadUrl?: string;
  multiple?: boolean;
  className?: string;
  containerClassName?: string;
  cropperProps?: Partial<ImgCropProps>;
  wide?: boolean;
  H140?: boolean;
  email?: boolean;
  H220?: boolean;
  H170?: boolean;
  H685?: boolean;
  allowToDelete?: boolean;
  allowToPreview?: boolean;
  label?: string;
  onLoadingChange?: (isLoading: boolean) => void;
  abortTrigger?: number;
}

interface PreviewModal {
  image: string;
  title: string;
  visible: boolean;
}
function ImageUpload({
  form,
  name,
  fileList,
  setFileList,
  required, accept,
  uploadUrl,
  multiple,
  className,
  containerClassName,
  cropperProps,
  wide,
  H220,
  H140,
  H685,
  H170,
  email,
  allowToDelete,
  allowToPreview,
  label,
  onLoadingChange,
}: InputCodeWrapperProps) {
  const { message } = App.useApp();
  const uploadRef = useRef<HTMLButtonElement | null>(null);
  const uploader = useUploadImage(uploadUrl);
  const [preview, setPreview] = useState<PreviewModal>({ visible: false, image: '', title: '' });

  useMessageError([uploader]);

  useEffect(() => {
    onLoadingChange?.(uploader.loading);
  }, [uploader.loading]);

  const cropperParams: Partial<ImgCropProps> = {
    rotationSlider: true,
    showReset: true,
    resetText: 'Сбросить',
    modalTitle: 'Редактировать фото',
    aspect: wide ? 16 / 9 : undefined,
    ...cropperProps,
  };

  /* useEffect(() => {
    if (uploader.data) {
      /!** When we get new data - set value in form *!/
      form.setFieldValue(name, uploader.data.url);
    }
  }, [uploader.data]); */

  /** Handlers */
  const handleCancelPreview = () => setPreview({ ...preview, visible: false });

  const onPreview = async (file: UploadFile & {timeStampString?: string;}) => {
    if (!file.url && !file.preview) {
      // eslint-disable-next-line no-param-reassign
      file.preview = await getBase64(file.originFileObj as Blob) as string;
    }

    setPreview({
      ...preview,
      image: file.url ? getFullFileUrl(file.url) : (file.preview || file.thumbUrl || ''),
      title: file.timeStampString || '',
      visible: true,
    });
  };

  const onRemove = (file: UploadFile) => {
    const index = fileList.indexOf(file);
    const newFileList = fileList.slice();

    newFileList.splice(index, 1);
    setFileList(newFileList);
  };

  const [replacingIndex, setReplacingIndex] = useState<number | null>(null);
  const onReplace = (file: UploadFile) => {
    const index = fileList.indexOf(file);

    setReplacingIndex(index);
    uploadRef.current?.click();
  };

  const beforeUpload = async (file: RcFile, FileList?: RcFile[], index?: number) => {
    /** Validation: */
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      message.error('Вы можете загрузить только файл JPG/PNG!');

      return false;
    }

    if (!(file.size / 1024 / 1024 < 10)) {
      message.error('Изображение должно быть меньше 10MB!');

      return false;
    }

    /** Functionality */
    const newFile: UploadFile = {
      originFileObj: file, uid: file.uid, name: file.name, status: 'done',
    };

    newFile.preview = await getBase64(file) as string;

    /** If function receive 'index' as prop, then we don't add new img at the end of array, but set in given position */
    if (index !== null && index !== undefined) {
      const newFileList = fileList.slice();

      newFileList[index] = newFile;
      setFileList(newFileList);
    } else /** If we dont have index, then do regular state update depends on 'is multiple' mode or not */
      if (multiple) {
        setFileList([...fileList, newFile]); // as UploadFile
      } else {
        setFileList([newFile]);
      }

    /** Upload image to API */
    const formData = new FormData();

    formData.append('file', newFile.originFileObj as Blob);
    uploader.fetch(formData)
      .then((res) => {
        if (res?.url) {
          setFileList((prevFileList) => {
            const updatedData = prevFileList.map((item) => (
              item.uid === file.uid ? { ...item, url: res.url } : item
            ));

            return updatedData;
          });
        }
      });

    return false;
  };

  const props: UploadProps = {
    /** Multiple should be false, to allow upload only one image at once. File list can be any length
     * - It 'multiple' prop from params will work anyway, because of 'beforeUpload' structure.
     * It have 'if (multiple)', which checks is need to replace current item or to add to the end of array */
    name,
    multiple: false,
    showUploadList: false,
    accept,
    onRemove,
    beforeUpload,
    fileList,
    listType: 'picture-card',
  };

  return (
    <div>
      <div className={clsx(styles.uploader, className, {
        [styles.singleUpload]: !multiple,
        [styles.wide]: wide,
        [styles.email]: email,
        [styles.H140]: H140,
        [styles.H220]: H220,
        [styles.H170]: H170,
        [styles.H685]: H685,
      })}
      >
        {fileList.map((file) => (
          <div key={file.uid} className={clsx(styles.imageContainer, styles.overlay, containerClassName)}>
            <div className={styles.contentInOverlay}>
              {allowToPreview ? (
                <EyeOutlined
                  className={styles.btn}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPreview(file);
                  }}
                  title="Просмотреть изображение"
                />
              ) : null}
              <EditOutlined
                className={styles.btn}
                onClick={(e) => {
                  e.stopPropagation();
                  onReplace(file);
                }}
                title="Заменить изображение"
              />
              {allowToDelete ? (
                <DeleteOutlined
                  className={styles.btn}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(file);
                  }}
                  title="Удалить изображение"
                />
              ) : null}
            </div>
            <img
              className={styles.uploadedImg}
              src={file.preview || getFullFileUrl(file.url) || file.thumbUrl}
              alt={file.name}
            />
          </div>
        ))}
        <Form.Item
          name={name}
          rules={required ? [{ required: true, message: messages.required }] : undefined}
          className="no-space-form-item"
        >
          <ImgCrop {...cropperParams}>
            <Upload
              {...props}
              className={styles.upload}
            >
              {!multiple && fileList.length >= 1 ? null : (
                <div className={styles.uploadBtn}>
                  <PlusOutlined />
                  {label || 'Загрузить'}
                </div>
              )}
            </Upload>
          </ImgCrop>
        </Form.Item>

        <ImgCrop {...cropperParams}>
          <Upload
            name="upload-for-replace"
            className={styles.hiddenUpload}
            beforeUpload={(file) => {
              if (replacingIndex !== null && replacingIndex !== undefined) {
                beforeUpload(file, undefined, replacingIndex);
                setReplacingIndex(null);
              }

              return false;
            }}
            accept={accept}
          >
            <Button ref={uploadRef}>
              Replace
            </Button>
          </Upload>
        </ImgCrop>

      </div>

      {uploader.error ? (
        <Alert
          style={{ marginTop: 8 }}
          type="error"
          message={getMessageInError(uploader.error)}
          closable
          onClose={uploader.clearError}
        />
      ) : null}

      <Modal title={preview.title} footer={null} centered open={preview.visible} onCancel={handleCancelPreview}>
        <img alt="preview" style={{ width: '100%' }} src={preview.image} className={styles.previewModalImage} />
      </Modal>
    </div>
  );
}

ImageUpload.defaultProps = {
  form: undefined,
  required: false,
  children: undefined,
  accept: '.jpg,.png,.jpeg',
  uploadUrl: 'common/upload-image',
  multiple: false,
  className: undefined,
  cropperProps: undefined,
  wide: false,
  H140: false,
  H170: undefined,
  email: false,
  H220: false,
  H685: false,
  allowToDelete: true,
  allowToPreview: true,
  label: 'Загрузить',
};

export default ImageUpload;
